import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { FormSubmission } from '../../types';
import { useLanguage } from '../../i18n/LanguageContext';

const PartnershipCarePlanSection: React.FC = () => {
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const { setValue, getValues } = useFormContext<FormSubmission>();
  const [isReady, setIsReady] = useState(false);
  const { language } = useLanguage();

  const FORM_SRC = language === 'es' ? '/partnership_careplan_es.html' : '/partnership_careplan.html';

  const isCheckbox = (el: Element): el is HTMLInputElement =>
    el.tagName === 'INPUT' && (el as HTMLInputElement).type === 'checkbox';

  // Collect all field values from iframe → form state
  const collectFromIframe = useCallback(() => {
    const doc = iframeRef.current?.contentWindow?.document;
    if (!doc) return;

    const flat: Record<string, string | boolean> = {};
    doc.querySelectorAll<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>(
      'input[id], textarea[id], select[id]'
    ).forEach((el) => {
      if (!el.id) return;
      if (isCheckbox(el)) {
        flat[el.id] = el.checked;
      } else {
        flat[el.id] = el.value ?? '';
      }
    });

    // Store ALL field values in the flat map for PDF generation
    (setValue as (path: string, value: any, opts?: any) => void)(
      'partnershipCarePlan_flat', flat, { shouldDirty: true }
    );

    // Sync key fields directly into carePlan / memberInfo paths
    const s = (id: string) => (flat[id] as string) || '';

    setValue('carePlan.leadCareManager.name',  s('ecm_staff_name'),  { shouldDirty: true });
    setValue('carePlan.leadCareManager.phone', s('ecm_staff_phone'), { shouldDirty: true });
    setValue('carePlan.leadCareManager.email', s('ecm_staff_email'), { shouldDirty: true });

    setValue('carePlan.emergencyContact.name',         s('emergency_name'),         { shouldDirty: true });
    setValue('carePlan.emergencyContact.relationship', s('emergency_relationship'), { shouldDirty: true });
    setValue('carePlan.emergencyContact.phone',        s('emergency_phone'),        { shouldDirty: true });

    // Goals I–IV
    const sv = setValue as (path: string, value: any, opts?: any) => void;
    for (let i = 1; i <= 4; i++) {
      const gi = i - 1;
      sv(`carePlan.problems.${gi}.problemName`, s(`goal_${i}_text`),        { shouldDirty: true });
      sv(`carePlan.problems.${gi}.startDate`,   s(`goal_${i}_target_date`), { shouldDirty: true });
      sv(`carePlan.problems.${gi}.comments`,    s(`goal_${i}_barriers`),    { shouldDirty: true });

      for (let j = 1; j <= 3; j++) {
        sv(`carePlan.problems.${gi}.goals.0.interventions.${j - 1}.interventionName`, s(`goal_${i}_step_${j}`),   { shouldDirty: true });
        sv(`carePlan.problems.${gi}.goals.0.interventions.${j - 1}.description`,      s(`goal_${i}_person_${j}`), { shouldDirty: true });
        sv(`carePlan.problems.${gi}.goals.0.interventions.${j - 1}.targetDate`,       s(`goal_${i}_date_${j}`),   { shouldDirty: true });
      }
    }
  }, [setValue, getValues]);

  // Push existing form values back into the iframe on load
  const applyToIframe = useCallback(() => {
    const doc = iframeRef.current?.contentWindow?.document;
    if (!doc) return;

    const setField = (id: string, value: string | boolean | undefined) => {
      const el = doc.getElementById(id) as HTMLInputElement | HTMLTextAreaElement | null;
      if (!el) return;
      if (el instanceof HTMLInputElement && el.type === 'checkbox') {
        el.checked = !!value;
      } else {
        (el as HTMLInputElement).value = value == null ? '' : String(value);
      }
    };

    // Restore all previously saved field values from the flat map first
    const savedFlat = getValues('partnershipCarePlan_flat');
    if (savedFlat) {
      Object.entries(savedFlat).forEach(([id, val]) => {
        setField(id, val);
      });
    }

    // Then overlay member info (always from the authoritative source)
    const cp   = getValues('carePlan');
    const info = getValues('memberInfo');

    setField('first_name',    info?.firstName || '');
    setField('last_name',     info?.lastName  || '');
    setField('cin',           info?.medicalId || '');
    setField('date_of_birth', info?.dateOfBirth || '');
    setField('phone',         info?.phone || '');
    setField('email',         info?.email || '');
    setField('address',       info?.address?.street   || '');
    setField('city',          info?.address?.city     || '');
    setField('state',         info?.address?.state    || 'CA');
    setField('zip',           info?.address?.zipCode  || '');
    setField('medi_cal_id',   info?.medicalId || '');

    setField('ecm_staff_name',  cp?.leadCareManager?.name  || '');
    setField('emergency_name',         cp?.emergencyContact?.name         || '');
    setField('emergency_relationship', cp?.emergencyContact?.relationship || '');
    setField('emergency_phone',        cp?.emergencyContact?.phone        || '');

    // Set signature image (img element — needs separate handling)
    const sig = getValues('signature');
    if (sig?.imageData) {
      const imgEl = doc.getElementById('ecm_signature_image') as HTMLImageElement | null;
      if (imgEl) {
        imgEl.src = sig.imageData;
        imgEl.style.display = 'block';
      }
    }

    const problems = cp?.problems || [];
    for (let i = 0; i < Math.min(problems.length, 4); i++) {
      const gi = i + 1;
      const p  = problems[i];
      setField(`goal_${gi}_text`,        p.problemName || '');
      setField(`goal_${gi}_target_date`, p.startDate   || '');
      setField(`goal_${gi}_barriers`,    p.comments    || '');
      const steps = p.goals?.[0]?.interventions || [];
      for (let j = 0; j < Math.min(steps.length, 3); j++) {
        setField(`goal_${gi}_step_${j + 1}`,   steps[j].interventionName || '');
        setField(`goal_${gi}_person_${j + 1}`, (steps[j] as any).description || '');
        setField(`goal_${gi}_date_${j + 1}`,   steps[j].targetDate || '');
      }
    }
  }, [getValues]);

  // Listen for changes inside the iframe
  useEffect(() => {
    if (!isReady) return;
    const doc = iframeRef.current?.contentWindow?.document;
    if (!doc) return;
    const handler = () => collectFromIframe();
    doc.addEventListener('input',  handler, true);
    doc.addEventListener('change', handler, true);
    return () => {
      doc.removeEventListener('input',  handler, true);
      doc.removeEventListener('change', handler, true);
    };
  }, [collectFromIframe, isReady]);

  const handleLoad = () => {
    setIsReady(true);
    applyToIframe();
    collectFromIframe();
  };

  const clearCheckboxes = () => {
    const doc = iframeRef.current?.contentWindow?.document;
    if (!doc) return;
    doc.querySelectorAll<HTMLInputElement>('input[type="checkbox"]').forEach((cb) => { cb.checked = false; });
    collectFromIframe();
  };

  const clearAllFields = () => {
    const doc = iframeRef.current?.contentWindow?.document;
    if (!doc) return;
    doc.querySelectorAll<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>(
      'input[id], textarea[id], select[id]'
    ).forEach((el) => {
      if (el instanceof HTMLInputElement && el.type === 'checkbox') el.checked = false;
      else (el as HTMLInputElement).value = '';
    });
    collectFromIframe();
  };

  return (
    <div className="space-y-4">
      <div className="rounded border border-purple-200 bg-purple-50 p-3 text-sm text-purple-900">
        {language === 'es'
          ? 'Complete el Plan de Atención ECM de Partnership HealthPlan a continuación. La información del miembro se completa previamente desde el paso anterior. Los valores se sincronizan automáticamente y se usan para generar el PDF.'
          : 'Fill the Partnership HealthPlan ECM Care Plan below. Member info is pre-filled from the previous step. Values are auto-synced and used to generate the PDF.'}
      </div>

      <div className="flex gap-2">
        <button
          type="button"
          onClick={clearCheckboxes}
          className="rounded bg-amber-100 px-3 py-1.5 text-sm font-medium text-amber-900 hover:bg-amber-200"
        >
          {language === 'es' ? 'Limpiar Casillas' : 'Clear Checkboxes'}
        </button>
        <button
          type="button"
          onClick={clearAllFields}
          className="rounded bg-red-100 px-3 py-1.5 text-sm font-medium text-red-900 hover:bg-red-200"
        >
          {language === 'es' ? 'Limpiar Todo' : 'Clear All Fields'}
        </button>
      </div>

      <iframe
        key={FORM_SRC}
        ref={iframeRef}
        title="Partnership Care Plan Form"
        src={FORM_SRC}
        onLoad={handleLoad}
        className="h-[80vh] w-full rounded border border-gray-300 bg-white"
      />
    </div>
  );
};

export default PartnershipCarePlanSection;
